import { onMount, onDestroy } from "@/modules/_";
import gsap from "@lib/gsap";
import { Draggable } from "gsap/Draggable";

// Register plugins
gsap.registerPlugin(Draggable);

export default function (element, dataset) {
  // console.log("key-data-points initialized", element);

  // Get references
  const icon = element.querySelector(".cc-recycle-bin");
  const modal = document.querySelector(".sotw-recycle_window");

  // console.log("Elements found:", { icon, modal });

  if (!icon || !modal) {
    console.warn("Required elements not found", { icon, modal });
    return;
  }

  // State
  let isOpen = false;
  let draggableInstance = null;
  let lastVelocityX = 0;
  let lastVelocityY = 0;

  // Store icon position for animation origin
  const getIconPosition = () => {
    const rect = icon.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  };

  // Open modal with genie effect
  const openModal = () => {
    if (isOpen) return;
    isOpen = true;

    // Get fresh positions for accurate centering
    const iconPos = getIconPosition();
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    const offsetX = iconPos.x - centerX;
    const offsetY = iconPos.y - centerY;

    // Set initial state - positioned at icon with 0 dimensions
    gsap.set(modal, {
      opacity: 0,
      width: 0,
      height: 0,
      left: "50%",
      top: "50%",
      xPercent: -50,
      yPercent: -50,
      x: offsetX,
      y: offsetY,
      display: "block",
      zIndex: 1000,
    });

    // Animate to full size at screen center with genie effect
    gsap.to(modal, {
      opacity: 1,
      width: 600,
      height: 400,
      x: 0,
      y: 0,
      duration: 0.6,
      ease: "back.out(1.2)",
      onComplete: () => {
        // Initialize draggable after animation with inertia
        if (draggableInstance) {
          draggableInstance.kill();
        }
        draggableInstance = Draggable.create(modal, {
          type: "x,y",
          bounds: window,
          edgeResistance: 0.65,
          onDrag: function () {
            // Track velocity during drag
            lastVelocityX = this.deltaX;
            lastVelocityY = this.deltaY;
          },
          onDragEnd: function () {
            // Apply inertia effect when drag ends
            const factor = 10; // How far the inertia carries
            const targetX = this.x + lastVelocityX * factor;
            const targetY = this.y + lastVelocityY * factor;

            gsap.to(modal, {
              x: targetX,
              y: targetY,
              duration: 0.8,
              ease: "power2.out",
              overwrite: "auto",
            });
          },
        })[0];

        // Add outside click listener after animation
        setTimeout(() => {
          document.addEventListener("click", handleOutsideClick);
        }, 100);
      },
    });

    console.log("Modal opened");
  };

  // Close modal with reverse genie effect
  const closeModal = () => {
    if (!isOpen) return;
    isOpen = false;

    // Remove outside click listener
    document.removeEventListener("click", handleOutsideClick);

    // Disable dragging during close animation
    if (draggableInstance) {
      draggableInstance.disable();
    }

    // Get fresh positions for accurate animation
    const iconPos = getIconPosition();
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    const offsetX = iconPos.x - centerX;
    const offsetY = iconPos.y - centerY;

    // Animate back to icon position with 0 dimensions
    gsap.to(modal, {
      opacity: 0,
      width: 0,
      height: 0,
      x: offsetX,
      y: offsetY,
      duration: 0.5,
      ease: "back.in(1.2)",
      onComplete: () => {
        gsap.set(modal, { display: "none" });
        if (draggableInstance) {
          draggableInstance.kill();
          draggableInstance = null;
        }
      },
    });

    console.log("Modal closed");
  };

  // Event handlers
  const handleIconClick = (e) => {
    console.log("Icon clicked!", { isOpen });
    e.preventDefault();
    e.stopPropagation();
    // Toggle modal
    if (isOpen) {
      closeModal();
    } else {
      openModal();
    }
  };

  const handleModalContentClick = (e) => {
    // Check if the clicked element is the minimize button or inside it
    const controls = modal.querySelector(".sotw-ui-controls");
    const minimizeBtn = e.target.closest(".cc-close");

    if (minimizeBtn && controls?.contains(minimizeBtn)) {
      console.log("Minimize button clicked via delegation!");
      e.preventDefault();
      e.stopPropagation();
      closeModal();
      return;
    }

    // Otherwise just prevent backdrop click
    e.stopPropagation();
  };

  const handleOutsideClick = (e) => {
    // Close if click is outside modal
    if (!modal.contains(e.target) && !icon.contains(e.target)) {
      closeModal();
    }
  };

  // Lifecycle: Mount
  onMount(() => {
    console.log("key-data-points mounted");

    // Set initial modal state
    gsap.set(modal, { display: "none", opacity: 0 });

    // Add event listeners
    console.log("Adding click listener to icon:", icon);
    icon.addEventListener("click", handleIconClick);

    // Use event delegation on the modal for all clicks (including minimize button)
    modal.addEventListener("click", handleModalContentClick);

    console.log("Event listeners attached");
  });

  // Lifecycle: Cleanup
  onDestroy(() => {
    console.log("key-data-points destroyed");

    // Remove event listeners
    icon.removeEventListener("click", handleIconClick);
    modal.removeEventListener("click", handleModalContentClick);
    document.removeEventListener("click", handleOutsideClick);

    // Kill draggable instance
    if (draggableInstance) {
      draggableInstance.kill();
      draggableInstance = null;
    }

    // Reset modal state
    gsap.set(modal, { clearProps: "all" });
  });
}
